import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import SideNavigation from '@cloudscape-design/components/side-navigation';
import AppLayoutCS from '@cloudscape-design/components/app-layout';
import Badge from '@cloudscape-design/components/badge';
import Input from '@cloudscape-design/components/input';
import { useAuth } from '../auth/AuthProvider';
import { useSearch } from '../search/SearchContext';
import { useTenant } from '../tenant/TenantContext';
import { api } from '../api';

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { query, setQuery, submitSearch, clearSearch } = useSearch();
  const { tenant, tenants, selectTenant } = useTenant();
  const [unreadCount, setUnreadCount] = useState(0);

  const activeHref = location.pathname === '/' ? '/' : location.pathname;

  // Fetch unread count for the badge. Re-runs on tenant change so the badge
  // reflects the newly selected mailbox rather than the previous one.
  useEffect(() => {
    api.listMessages({ limit: 100, status: 'unread' })
      .then((res) => setUnreadCount(res.items.length))
      .catch(() => {});
  }, [location, tenant?.tenantId]);

  /**
   * Mailbox dropdown items: every tenant from the directory, switched inline.
   * The active tenant is marked with a check. There is deliberately no route
   * back to the picker — it is a first-sign-in screen only.
   */
  const mailboxItems = tenants.map((t) => ({
    id: t.tenantId,
    text: t.displayName || t.tenantId,
    description: t.mailboxAddress || t.domain || undefined,
    iconName: t.tenantId === tenant?.tenantId ? ('check' as const) : undefined,
  }));

  function handleMailboxSelect(id: string) {
    const next = tenants.find((t) => t.tenantId === id);
    // Selecting the active tenant is a no-op; avoids a pointless refetch.
    if (next && next.tenantId !== tenant?.tenantId) {
      selectTenant(next);
      clearSearch();
      navigate('/');
    }
  }

  function handleSearch() {
    // Inline search — filters the inbox in place. Navigate home if the user
    // is on another view so they can see the filtered results.
    submitSearch();
    if (location.pathname !== '/') {
      navigate('/');
    }
  }

  return (
    <>
      <TopNavigation
        identity={{
          href: '/',
          title: 'MailHub',
          logo: { src: '/mailhub-icon.svg', alt: 'MailHub' },
        }}
        search={
          <Input
            type="search"
            placeholder="Search mail…"
            value={query}
            onChange={({ detail }) => {
              setQuery(detail.value);
              // Clearing the box restores the full inbox immediately
              if (detail.value === '') clearSearch();
            }}
            onKeyDown={({ detail }) => {
              if (detail.key === 'Enter') handleSearch();
            }}
          />
        }
        utilities={[
          {
            type: 'menu-dropdown',
            text: tenant?.displayName || tenant?.tenantId || 'Select mailbox',
            description: tenant?.mailboxAddress || tenant?.domain || undefined,
            iconName: 'envelope',
            items: mailboxItems,
            onItemClick: ({ detail }) => handleMailboxSelect(detail.id),
          },
          {
            type: 'menu-dropdown',
            text: user?.alias || 'User',
            description: user?.email || '',
            iconName: 'user-profile',
            items: [
              { id: 'profile', text: `Alias: ${user?.alias || 'unknown'}` },
              { id: 'signout', text: 'Sign out' },
            ],
            onItemClick: ({ detail }) => {
              if (detail.id === 'signout') signOut();
            },
          },
        ]}
      />
      <AppLayoutCS
        headerSelector="#h"
        navigation={
          <SideNavigation
            activeHref={activeHref}
            onFollow={(event) => {
              event.preventDefault();
              navigate(event.detail.href);
            }}
            header={{ text: 'Mailbox', href: '/' }}
            items={[
              {
                type: 'link',
                text: 'Inbox',
                href: '/',
                info: unreadCount > 0 ? <Badge color="red">{String(unreadCount)}</Badge> : undefined,
              },
              { type: 'divider' },
              { type: 'link', text: 'Rules', href: '/rules' },
              { type: 'link', text: 'Settings', href: '/settings' },
            ]}
          />
        }
        content={<Outlet />}
        toolsHide={true}
        navigationWidth={200}
      />
    </>
  );
}
